import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, ArrowDown, ArrowUp, Pencil, Plus, Trash2, Undo2 } from 'lucide-react';
import {
  LMS_QUESTION_TYPES,
  LmsMcqMultiContent,
  LmsMcqSingleContent,
  LmsQuestion,
  LmsQuestionType,
  LmsQuestionsApi,
  LmsQuizzesApi,
  LmsShortAnswerContent,
  LmsTrueFalseContent,
} from '@/services/lms.api';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea, Checkbox } from '@/components/ui/Input';
import { toast } from '@/stores/toast.store';

const TYPE_LABEL: Record<LmsQuestionType, string> = {
  mcq_single: 'Single choice',
  mcq_multi: 'Multiple choice',
  true_false: 'True / False',
  short_answer: 'Short answer',
};

const LETTERS = 'abcdefghij'.split('');

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

export function LmsQuizQuestionsModal({
  quizId,
  quizTitle,
  onClose,
}: {
  quizId: string;
  quizTitle: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [questionModal, setQuestionModal] = useState<{ open: boolean; question?: LmsQuestion }>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<LmsQuestion | null>(null);

  const { data: quiz, isLoading } = useQuery({
    queryKey: ['lms-quiz-detail', quizId],
    queryFn: () => LmsQuizzesApi.get(quizId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['lms-quiz-detail', quizId] });
    qc.invalidateQueries({ queryKey: ['lms-quizzes'] });
  };

  const upsert = useMutation({
    mutationFn: (v: { id?: string; payload: ReturnType<typeof buildPayload> }) =>
      v.id ? LmsQuestionsApi.update(quizId, v.id, v.payload) : LmsQuestionsApi.create(quizId, v.payload),
    onSuccess: () => {
      invalidate();
      setQuestionModal({ open: false });
      toast.success('Question saved');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: (questionId: string) => LmsQuestionsApi.remove(quizId, questionId),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
      toast.success('Question deleted');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Delete failed'),
  });

  const reorder = useMutation({
    mutationFn: (orderedQuestionIds: string[]) => LmsQuestionsApi.reorder(quizId, orderedQuestionIds),
    onSuccess: invalidate,
    onError: (e) => toast.error(errMsg(e) ?? 'Reorder failed'),
  });

  const unpublish = useMutation({
    mutationFn: () => LmsQuizzesApi.unpublish(quizId),
    onSuccess: () => {
      invalidate();
      toast.success('Quiz unpublished');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Unpublish failed'),
  });

  const questions = quiz?.questions ?? [];
  const locked = quiz?.status === 'published';

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    const ids = questions.map((q) => q.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids);
  };

  return (
    <Modal open onClose={onClose} title={`Questions — ${quizTitle}`} size="xl">
      {isLoading ? (
        <div className="py-8 text-center text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-4">
          {locked && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p>This quiz is published — questions are read-only. Unpublish the quiz to edit questions.</p>
              </div>
              <button
                className="btn-secondary shrink-0"
                onClick={() => unpublish.mutate()}
                disabled={unpublish.isPending}
              >
                <Undo2 className="mr-1.5 h-4 w-4" /> Unpublish
              </button>
            </div>
          )}

          {questions.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400">
              No questions yet.
            </div>
          ) : (
            <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
              {questions.map((q, index) => (
                <li key={q.id} className="flex items-center gap-3 px-3 py-3">
                  {!locked && (
                    <div className="flex flex-col">
                      <button
                        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        onClick={() => moveQuestion(index, -1)}
                        disabled={index === 0}
                        title="Move up"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        onClick={() => moveQuestion(index, 1)}
                        disabled={index === questions.length - 1}
                        title="Move down"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <Badge tone="slate">{TYPE_LABEL[q.type]}</Badge>
                  <span className="flex-1 truncate text-sm text-slate-800" title={q.prompt}>
                    {q.prompt}
                  </span>
                  <span className="shrink-0 text-xs text-slate-400">{q.marks} mark{q.marks === 1 ? '' : 's'}</span>
                  {!locked && (
                    <>
                      <button
                        className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                        onClick={() => setQuestionModal({ open: true, question: q })}
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                        onClick={() => setConfirmDelete(q)}
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          {!locked && (
            <button className="btn-secondary" onClick={() => setQuestionModal({ open: true })}>
              <Plus className="mr-1.5 h-4 w-4" /> Add question
            </button>
          )}
        </div>
      )}

      {questionModal.open && (
        <QuestionFormModal
          open={questionModal.open}
          question={questionModal.question}
          onClose={() => setQuestionModal({ open: false })}
          saving={upsert.isPending}
          onSubmit={(v) => upsert.mutate({ id: questionModal.question?.id, payload: v })}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        loading={remove.isPending}
        title="Delete question?"
        message={`Delete this ${confirmDelete ? TYPE_LABEL[confirmDelete.type].toLowerCase() : ''} question.`}
        confirmText="Delete question"
      />
    </Modal>
  );
}

const optionSchema = z.object({ text: z.string() });

const schema = z
  .object({
    type: z.enum(LMS_QUESTION_TYPES),
    prompt: z.string().min(1, 'Required'),
    marks: z.string().min(1, 'Required'),
    options: z.array(optionSchema),
    correctIndex: z.number().nullable(),
    correctIndices: z.array(z.number()),
    correctAnswerBool: z.enum(['true', 'false', '']),
    expectedAnswer: z.string().optional().or(z.literal('')),
    caseSensitive: z.boolean(),
  })
  .superRefine((v, ctx) => {
    const marksNum = Number(v.marks);
    if (!Number.isInteger(marksNum) || marksNum < 1) {
      ctx.addIssue({ path: ['marks'], code: z.ZodIssueCode.custom, message: 'Must be a positive whole number' });
    }
    if (v.type === 'mcq_single' || v.type === 'mcq_multi') {
      if (v.options.length < 2) {
        ctx.addIssue({ path: ['options'], code: z.ZodIssueCode.custom, message: 'At least 2 options are required' });
      }
      if (v.options.length > 10) {
        ctx.addIssue({ path: ['options'], code: z.ZodIssueCode.custom, message: 'At most 10 options are allowed' });
      }
      v.options.forEach((o, i) => {
        if (!o.text.trim()) {
          ctx.addIssue({ path: ['options', i, 'text'], code: z.ZodIssueCode.custom, message: 'Required' });
        }
      });
    }
    if (v.type === 'mcq_single' && (v.correctIndex == null || v.correctIndex >= v.options.length)) {
      ctx.addIssue({ path: ['correctIndex'], code: z.ZodIssueCode.custom, message: 'Mark the correct option' });
    }
    if (v.type === 'mcq_multi' && v.correctIndices.filter((i) => i < v.options.length).length < 1) {
      ctx.addIssue({ path: ['correctIndices'], code: z.ZodIssueCode.custom, message: 'Mark at least one correct option' });
    }
    if (v.type === 'true_false' && v.correctAnswerBool === '') {
      ctx.addIssue({ path: ['correctAnswerBool'], code: z.ZodIssueCode.custom, message: 'Select True or False' });
    }
  });
type FormValues = z.infer<typeof schema>;

function emptyOption() {
  return { text: '' };
}

function defaultsFromQuestion(question?: LmsQuestion): FormValues {
  if (!question) {
    return {
      type: 'mcq_single',
      prompt: '',
      marks: '',
      options: [emptyOption(), emptyOption()],
      correctIndex: null,
      correctIndices: [],
      correctAnswerBool: '',
      expectedAnswer: '',
      caseSensitive: false,
    };
  }
  const base: FormValues = {
    type: question.type,
    prompt: question.prompt,
    marks: String(question.marks),
    options: [emptyOption(), emptyOption()],
    correctIndex: null,
    correctIndices: [],
    correctAnswerBool: '',
    expectedAnswer: '',
    caseSensitive: false,
  };
  if (question.type === 'mcq_single') {
    const c = question.content as LmsMcqSingleContent;
    base.options = c.options.map((o) => ({ text: o.text }));
    base.correctIndex = c.options.findIndex((o) => o.id === c.correctOptionId);
  } else if (question.type === 'mcq_multi') {
    const c = question.content as LmsMcqMultiContent;
    base.options = c.options.map((o) => ({ text: o.text }));
    base.correctIndices = c.options
      .map((o, i) => (c.correctOptionIds.includes(o.id) ? i : -1))
      .filter((i) => i >= 0);
  } else if (question.type === 'true_false') {
    const c = question.content as LmsTrueFalseContent;
    base.correctAnswerBool = c.correctAnswer ? 'true' : 'false';
  } else if (question.type === 'short_answer') {
    const c = question.content as LmsShortAnswerContent;
    base.expectedAnswer = c.expectedAnswer ?? '';
    base.caseSensitive = c.caseSensitive ?? false;
  }
  return base;
}

/** Option ids are assigned fresh at submit time as sequential letters
 * (a, b, c, …) by final row order — the backend's `id` contract is just
 * "non-empty, unique string" (see the DTO's own doc comment), so there's
 * no need to carry a stable id across add/remove/reorder edits client-side.
 * Correctness is tracked by row INDEX in the form and only mapped to a
 * letter at the very end. */
function buildContent(v: FormValues) {
  switch (v.type) {
    case 'mcq_single': {
      const options = v.options.map((o, i) => ({ id: LETTERS[i], text: o.text }));
      return { options, correctOptionId: LETTERS[v.correctIndex as number] };
    }
    case 'mcq_multi': {
      const options = v.options.map((o, i) => ({ id: LETTERS[i], text: o.text }));
      return { options, correctOptionIds: v.correctIndices.map((i) => LETTERS[i]) };
    }
    case 'true_false':
      return { correctAnswer: v.correctAnswerBool === 'true' };
    case 'short_answer':
      return {
        expectedAnswer: v.expectedAnswer?.trim() || undefined,
        caseSensitive: v.caseSensitive,
      };
  }
}

function buildPayload(v: FormValues) {
  return {
    type: v.type,
    prompt: v.prompt,
    marks: Number(v.marks),
    content: buildContent(v),
  };
}

const radioClass =
  'h-4 w-4 rounded-full border-slate-300 text-brand-600 focus:ring-brand-500';

function QuestionFormModal({
  open,
  question,
  onClose,
  onSubmit,
  saving,
}: {
  open: boolean;
  question?: LmsQuestion;
  onClose: () => void;
  onSubmit: (v: ReturnType<typeof buildPayload>) => void;
  saving: boolean;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: defaultsFromQuestion(question),
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'options' });
  const type = watch('type');
  const correctIndex = watch('correctIndex');
  const correctIndices = watch('correctIndices');

  const toggleMulti = (i: number) => {
    const next = correctIndices.includes(i) ? correctIndices.filter((x) => x !== i) : [...correctIndices, i];
    setValue('correctIndices', next, { shouldValidate: true });
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={question ? 'Edit question' : 'Add question'}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit((v) => onSubmit(buildPayload(v)))}
            disabled={saving}
          >
            {saving ? 'Saving…' : question ? 'Save changes' : 'Add question'}
          </button>
        </>
      }
    >
      <form className="space-y-4">
        <Field label="Type" required hint={question ? "Type can't be changed after creation" : undefined}>
          {question ? (
            <Input value={TYPE_LABEL[question.type]} disabled />
          ) : (
            <Select {...register('type')}>
              {LMS_QUESTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Prompt" required error={errors.prompt?.message}>
          <Textarea rows={2} {...register('prompt')} placeholder="Question text…" />
        </Field>

        <Field label="Marks" required error={errors.marks?.message}>
          <Input type="number" min={1} {...register('marks')} className="max-w-[140px]" />
        </Field>

        {(type === 'mcq_single' || type === 'mcq_multi') && (
          <Field
            label="Options"
            required
            error={(errors.options?.message as string) || (errors.correctIndex?.message as string) || (errors.correctIndices?.message as string)}
            hint={type === 'mcq_single' ? 'Mark the one correct option' : 'Mark at least one correct option'}
          >
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={f.id} className="flex items-center gap-2">
                  {type === 'mcq_single' ? (
                    <input
                      type="radio"
                      className={radioClass}
                      checked={correctIndex === i}
                      onChange={() => setValue('correctIndex', i, { shouldValidate: true })}
                    />
                  ) : (
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      checked={correctIndices.includes(i)}
                      onChange={() => toggleMulti(i)}
                    />
                  )}
                  <Input {...register(`options.${i}.text` as const)} placeholder={`Option ${LETTERS[i]?.toUpperCase()}`} />
                  <button
                    type="button"
                    className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                    onClick={() => {
                      remove(i);
                      if (type === 'mcq_single' && correctIndex === i) setValue('correctIndex', null);
                      if (type === 'mcq_multi') {
                        setValue(
                          'correctIndices',
                          correctIndices.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)),
                        );
                      }
                    }}
                    disabled={fields.length <= 2}
                    title="Remove option"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => append(emptyOption())}
                disabled={fields.length >= 10}
              >
                <Plus className="mr-1.5 h-4 w-4" /> Add option
              </button>
            </div>
          </Field>
        )}

        {type === 'true_false' && (
          <Field label="Correct answer" required error={errors.correctAnswerBool?.message}>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="radio" className={radioClass} value="true" {...register('correctAnswerBool')} />
                True
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="radio" className={radioClass} value="false" {...register('correctAnswerBool')} />
                False
              </label>
            </div>
          </Field>
        )}

        {type === 'short_answer' && (
          <>
            <Field label="Expected answer" hint="Optional — shown to staff as a grading reference">
              <Input {...register('expectedAnswer')} placeholder="e.g. Photosynthesis" />
            </Field>
            <Checkbox label="Case-sensitive" {...register('caseSensitive')} />
          </>
        )}
      </form>
    </Modal>
  );
}
